<template>
  <div class="home-container">
        <!-- 主欢迎区域 -->
        <div class="welcome-section">
          <div class="welcome-content">
            <h1 class="welcome-title">欢迎使用弘笃智慧云管理系统</h1>
            <p class="welcome-subtitle">高效、安全、便捷的管理平台</p>
            <div class="welcome-meta">
              <a-tag color="success" style="color: #333">系统运行正常</a-tag>
              <a-tag color="processing" style="color: #333; background-color: white; border-color: #d9d9d9;">版本 v1.0.0</a-tag>
            </div>
          </div>
        </div>

        <!-- 内容区域 - 优化排版 -->
        <div class="content-layout">
          <a-row :gutter="24">
            <!-- 左侧内容区域 -->
            <a-col :span="16">
              <div class="left-content">
                <!-- 温馨提醒卡片 -->
                <a-card
                  title="温馨提醒"
                  class="content-card enhanced-card"
                  :bordered="false"
                >
                  <div class="card-content">
                    <a-list :data-source="reminders" :split="false">
                      <template #renderItem="{ item }">
                        <a-list-item>
                          <a-list-item-meta>
                            <template #avatar>
                              <div>
                                <component
                                  :is="item.icon"
                                  :style="{
                                    color: item.color,
                                    fontSize: '20px',
                                  }"
                                />
                              </div>
                            </template>
                            <template #title>
                              <div class="reminder-title">{{ item.title }}</div>
                            </template>
                            <template #description>
                              <div>
                                <p class="reminder-date">{{ item.date }}</p>
                                <p class="reminder-content">
                                  {{ item.content }}
                                </p>
                              </div>
                            </template>
                          </a-list-item-meta>
                        </a-list-item>
                      </template>
                    </a-list>
                  </div>
                </a-card>

                <!-- 系统状态卡片 -->
                <a-card
                  title="系统状态"
                  class="content-card enhanced-card"
                  :bordered="false"
                  style="margin-top: 24px"
                >
                  <div class="card-content">
                    <a-row :gutter="16">
                      <a-col :span="12">
                        <div class="status-block">
                          <div class="status-icon-wrapper">
                            <CheckCircleOutlined
                              twoToneColor="#52c41a"
                              class="status-main-icon"
                              style="color: #52c41a; font-size: 24px;"
                            />
                          </div>
                          <div class="status-info">
                            <h4>运行状态</h4>
                            <p class="status-value success">系统运行正常</p>
                          </div>
                        </div>
                      </a-col>
                      <a-col :span="12">
                        <div class="status-block">
                          <div class="status-icon-wrapper">
                            <SyncOutlined spin class="status-main-icon" style="font-size: 24px;" />
                          </div>
                          <div class="status-info">
                            <h4>最后更新</h4>
                            <p class="status-value">
                              {{ currentTime.toLocaleTimeString() }}
                            </p>
                          </div>
                        </div>
                      </a-col>
                    </a-row>
                  </div>
                </a-card>
              </div>
            </a-col>

            <!-- 右侧内容区域 -->
            <a-col :span="8">
              <div class="right-content">
                <!-- 技术支持卡片 -->
                <a-card
                  title="技术支持"
                  class="content-card enhanced-card"
                  :bordered="false"
                >
                  <div class="card-content">
                    <div class="support-section">
                      <h4>
                        <PhoneOutlined class="section-icon" /> 联系电话
                      </h4>
                      <p class="support-detail">18652016876</p>
                    </div>
                    <div class="support-section">
                      <h4>
                        <MailOutlined class="section-icon" /> 邮箱地址
                      </h4>
                      <p class="support-detail">841656826@qq.com</p>
                    </div>
                    <div class="support-section">
                      <h4>
                        <ClockCircleOutlined class="section-icon" />
                        服务时间
                      </h4>
                      <p class="support-detail">周一至周五</p>
                      <p class="support-detail">9:30 - 18:00</p>
                    </div>
                  </div>
                </a-card>

                <!-- 版本信息卡片 -->
                <a-card
                  title="版本信息"
                  class="content-card enhanced-card"
                  :bordered="false"
                  style="margin-top: 24px"
                >
                  <div class="card-content">
                    <div class="version-section">
                      <div class="version-badge">
                        <a-badge status="success" />
                        <span>v1.0.0</span>
                      </div>
                      <p class="version-desc">当前为稳定版本</p>
                      <!--                      <div class="version-date">发布日期：2024-01-15</div>-->
                    </div>
                  </div>
                </a-card>
              </div>
            </a-col>
          </a-row>
        </div>

        <!-- 底部信息 -->
        <div class="footer-info">
          <a-divider />
          <div class="system-info">
            <p>后台管理系统 - 让管理更简单</p>
          </div>
        </div>
      </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { 
  Row, 
  Col, 
  Card, 
  Divider, 
  Tag, 
  List, 
  Badge 
} from 'ant-design-vue'
import {
  SecurityScanOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  PhoneOutlined,
  MailOutlined,
  ClockCircleOutlined
} from '@ant-design/icons-vue'

const currentTime = ref(new Date())

const currentDate = computed(() => {
  return currentTime.value.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  })
})

const reminders = computed(() => [
  {
    icon: SecurityScanOutlined,
    color: '#1890ff',
    title: '安全提醒',
    date: currentDate.value,
    content: '请定期修改密码，确保账户安全。',
  },
  {
    icon: BulbOutlined,
    color: '#faad14',
    title: '使用建议',
    date: currentDate.value,
    content: '建议使用最新版本的Chrome浏览器以获得最佳体验。',
  },
])

let timeInterval = null

onMounted(() => {
  // 每分钟更新时间
  timeInterval = setInterval(() => {
    currentTime.value = new Date()
  }, 60000)

})

onUnmounted(() => {
  if (timeInterval) {
    clearInterval(timeInterval)
  }
})
</script>

<style lang="less" scoped>
.home-container {
  padding: 24px;
}
.welcome-section {
  background: #FFFFFF;
  border-radius: 12px;
  padding: 48px 40px;
  margin-bottom: 24px;
  color: #333;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  .welcome-title {
    font-size: 32px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #165DFF;
  }

  .welcome-subtitle {
    font-size: 18px;
    opacity: 0.9;
    margin-bottom: 24px;
  }

  .welcome-meta {
    .ant-tag {
      margin: 0 8px;
    }
    /* 版本标签白色背景 */
    .ant-tag[color="processing"] {
      background-color: white !important;
      border-color: #d9d9d9 !important;
    }
  }
}
.content-layout {
  margin-bottom: 24px;
  .enhanced-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: all 0.3s;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }

    ::v-deep .ant-card-head {
      border-bottom: 1px solid #f0f0f0;
      padding: 0 24px;

      .ant-card-head-title {
        font-size: 16px;
        font-weight: 500;
        padding: 16px 0;
      }
    }

    ::v-deep .ant-card-body {
      padding: 24px;
    }
  }

  .left-content,
  .right-content {
    .card-content {
      .reminder-title {
        font-weight: 500;
        color: #333;
        margin-bottom: 4px;
      }

      .reminder-date {
        color: #999;
        font-size: 12px;
        margin: 4px 0;
      }

      .reminder-content {
        color: #666;
        margin: 8px 0 0 0;
        line-height: 1.5;
      }

      .status-block {
        display: flex;
        align-items: center;
        padding: 16px;
        background: #fafafa;
        border-radius: 6px;
        transition: all 0.3s;

        &:hover {
          background: #f0faff;
        }

        .status-icon-wrapper {
          margin-right: 16px;

          .status-main-icon {
            font-size: 24px;
          }
        }

        .status-info {
          h4 {
            margin: 0 0 8px 0;
            font-size: 14px;
            color: #666;
          }

          .status-value {
            margin: 0;
            font-weight: 500;
            color: #333;

            &.success {
              color: #52c41a;
            }
          }
        }
      }

      .support-section {
        margin-bottom: 24px;

        &:last-child {
          margin-bottom: 0;
        }

        .section-icon {
          margin-right: 8px;
          color: #1890ff;
        }

        h4 {
          margin: 0 0 8px 0;
          font-size: 14px;
          color: #333;
        }

        .support-detail {
          margin: 0 0 4px 0;
          color: #666;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      .version-section {
        text-align: center;

        .version-badge {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 12px;

          span {
            margin-left: 8px;
            font-weight: 500;
            font-size: 16px;
            color: #333;
          }
        }

        .version-desc {
          margin: 0 0 8px 0;
          color: #666;
        }

        .version-date {
          color: #999;
          font-size: 12px;
        }
      }
    }
  }
}
.footer-info {
  text-align: center;
  margin-top: 24px;
  .system-info {
    p {
      color: #999;
      margin-bottom: 0;
    }
  }
}
</style>
